import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

export interface TurnstileHandle {
  execute: () => Promise<string>;
  reset: () => void;
}

interface TurnstileWidgetProps {
  onToken?: (token: string | null) => void;
  siteKey?: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  size?: 'normal' | 'compact' | 'invisible';
  className?: string;
}

type PendingPromise = {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
};

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          size?: 'normal' | 'compact' | 'invisible';
          appearance?: 'always' | 'execute' | 'interaction-only';
          execution?: 'render' | 'execute';
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          retry?: 'never' | 'auto';
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
      execute: (widgetId: string) => void;
    };
  }
}

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const TurnstileWidget = React.forwardRef<TurnstileHandle, TurnstileWidgetProps>(
  ({ onToken, siteKey, appearance = 'execute', size = 'invisible', className }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>();
  const pendingRef = useRef<PendingPromise | null>(null);
  const [ready, setReady] = useState(false);

  const cleanupWidget = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = undefined;
    }
    pendingRef.current = null;
  }, []);

  useImperativeHandle(ref, () => ({
    execute: () => {
      return new Promise<string>((resolve, reject) => {
        if (!ready || !window.turnstile || !widgetIdRef.current) {
          reject(new Error('Turnstile is not ready'));
          return;
        }

        pendingRef.current = { resolve, reject };
        window.turnstile.execute(widgetIdRef.current);
      });
    },
    reset: () => {
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current);
      }
      pendingRef.current = null;
      onToken?.(null);
    }
  }), [onToken, ready]);

  useEffect(() => {
    const resolvedSiteKey = siteKey ?? process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '';
    if (!resolvedSiteKey) {
      console.warn('[Turnstile] Missing site key. Set REACT_APP_TURNSTILE_SITE_KEY.');
      return;
    }

    const renderWidget = () => {
      if (!containerRef.current || !window.turnstile) {
        return;
      }
      cleanupWidget();
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: resolvedSiteKey,
        size,
        appearance,
        execution: 'execute',
        retry: 'auto',
        callback: (token: string) => {
          onToken?.(token);
          if (pendingRef.current) {
            pendingRef.current.resolve(token);
            pendingRef.current = null;
          }
        },
        'error-callback': () => {
          onToken?.(null);
          if (pendingRef.current) {
            pendingRef.current.reject(new Error('Turnstile error'));
            pendingRef.current = null;
          }
        },
        'expired-callback': () => {
          onToken?.(null);
          pendingRef.current = null;
        }
      });
      setReady(true);
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      if (!document.getElementById(SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          renderWidget();
        };
        script.onerror = () => {
          console.error('[Turnstile] Failed to load script.');
        };
        document.body.appendChild(script);
      } else {
        const existingScript = document.getElementById(SCRIPT_ID);
        existingScript?.addEventListener('load', renderWidget, { once: true });
      }
    }

    return () => {
      cleanupWidget();
      setReady(false);
    };
  }, [appearance, cleanupWidget, onToken, siteKey, size]);

  return <div ref={containerRef} className={className} />;
});

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;
